import React, { useEffect, useState } from "react";
import { trackProfileEvent } from "../lib/analytics";

function ModalShell({ id, title, onClose, children, footer }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="xs-modal-backdrop"
      onClick={onClose}
      data-testid={`${id}-modal-backdrop`}
    >
      <div
        className="xs-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${id}-modal`}
        role="dialog"
        aria-modal="true"
      >
        <div className="xs-modal-header">
          <span>{title}</span>
          <button
            className="xs-modal-close"
            onClick={onClose}
            data-testid={`${id}-modal-close`}
            aria-label="Close"
          >
            [x]
          </button>
        </div>
        <div className="xs-modal-body">{children}</div>
        <div className="xs-modal-footer">
          {footer || (
            <button
              className="xs-btn"
              onClick={onClose}
              data-testid={`${id}-modal-return`}
            >
              return
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AboutModal({ onClose }) {
  return (
    <ModalShell id="about" title="about xaelserpent" onClose={onClose}>
      <p>
        <strong>xaelserpent</strong> is a visionary hub of{" "}
        <strong>streaming</strong>, <strong>publishing</strong>, and{" "}
        <strong>innovation</strong>. Led by <strong>Dan Ryder</strong>, we
        stream electrifying gaming and community experiences.
      </p>
      <p>
        As publishers, we own rights to unique digital art and craft narratives
        that captivate imaginations, inspiring the next generation of creators
        and storytellers.
      </p>
      <p>
        On <strong>xaelserpent.com</strong>, we develop tools and platforms to
        empower creators' visions and ideas — built for those who imagine more.
      </p>
      <hr style={{ border: 0, borderTop: "1px solid #ffde16", margin: "20px 0" }} />
      <p>
        <strong>For business inquiries</strong>, e-mail us at:{" "}
        <a href="mailto:xaelserpent@gmail.com">xaelserpent@gmail.com</a>
        <br />
        <small>We do not respond to direct messages on most social media platforms.</small>
      </p>
    </ModalShell>
  );
}

export function PioneerCardModal({ onClose }) {
  return (
    <ModalShell
      id="pioneer-card"
      title="xaelserpent contact card"
      onClose={onClose}
      footer={(
        <div className="xs-pioneer-modal-actions">
          <a
            className="xs-btn"
            href="/pioneercard.png"
            download="xaelserpent-pioneer-contact-card.png"
            data-testid="pioneer-card-download"
          >
            download contact card
          </a>
          <button
            className="xs-btn"
            type="button"
            onClick={onClose}
            data-testid="pioneer-card-modal-return"
          >
            close
          </button>
        </div>
      )}
    >
      <p className="xs-pioneer-modal-hint">
        select the card to visit xaelserpent.com, or download a copy below.
      </p>
      <a
        className="xs-pioneer-modal-image-link"
        href="https://xaelserpent.com/"
        onClick={onClose}
        data-testid="pioneer-card-site-link"
      >
        <img
          src="/pioneercard.png"
          alt="Developed and managed by Dan Ryder — xaelserpent contact card"
        />
      </a>
    </ModalShell>
  );
}

function ProfileModuleModal({ module, onClose }) {
  const profile = module.content || {};
  const [warning, setWarning] = useState("");

  const isAdminView = () => typeof window !== "undefined" && Boolean(sessionStorage.getItem("xs_admin_token"));

  useEffect(() => {
    if (isAdminView()) return;
    const key = `xs_profile_viewed_${module.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    trackProfileEvent("profile_view", module.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id, module.key]);

  const reportGesture = (gesture) => {
    if (isAdminView()) return;
    setWarning("Saving this photo is not allowed. This profile is for viewing on this site only.");
    trackProfileEvent("profile_save_attempt", module.key, gesture);
    const timer = window.setTimeout(() => setWarning(""), 4000);
    return () => window.clearTimeout(timer);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "PrintScreen") reportGesture("printscreen");
      else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "s") reportGesture("keycombo");
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") reportGesture("blur");
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blockContext = (e) => { e.preventDefault(); reportGesture("contextmenu"); };
  const blockDrag = (e) => { e.preventDefault(); reportGesture("dragstart"); };

  const photoSrc = profile.photo_url || "/profile-anonymous.svg";

  return (
    <ModalShell id={module.key} title={module.label} onClose={onClose}>
      <div className="xs-profile-panel xs-profile-panel-responsive">
        <div className="xs-profile-photo-frame" onContextMenu={blockContext} onDragStart={blockDrag}>
          <img
            className="xs-profile-photo"
            src={photoSrc}
            alt={profile.real_name || "Anonymous profile"}
            draggable={false}
            style={{ userSelect: "none", WebkitUserDrag: "none" }}
          />
          <div className="xs-profile-photo-overlay" aria-hidden="true" />
        </div>
        {warning && <div className="xs-profile-warning" role="alert">{warning}</div>}
        <h2>{profile.real_name}</h2>
        {profile.age && <div><strong>Age:</strong> {profile.age}</div>}
        {profile.occupation && <div><strong>Occupation:</strong> {profile.occupation}</div>}
        {profile.tagline && <p className="xs-profile-tagline">{profile.tagline}</p>}
        {profile.bio && <div className="xs-header-module-content" dangerouslySetInnerHTML={{ __html: profile.bio }} />}
        {Array.isArray(profile.links) && profile.links.length > 0 && (
          <div className="xs-profile-links">
            {profile.links.map((link) => <a key={`${link.label}-${link.url}`} href={link.url} target="_blank" rel="noreferrer">{link.label}</a>)}
          </div>
        )}
      </div>
    </ModalShell>
  );
}

export function ScheduleModal({ onClose }) {
  return (
    <ModalShell
      id="schedule"
      title="streaming schedule | twitch.tv or kick.com"
      onClose={onClose}
    >
      <p>
        <strong>Location:</strong> Toronto, Ontario, Canada
        <br />
        <strong>Time Zone:</strong> Eastern Standard Time (EST)
        <br />
        <strong>Host:</strong> Dan Ryder
      </p>
      <ul>
        <li><strong>Sunday:</strong> Day-Off, No Stream</li>
        <li><strong>Monday:</strong> Day-Off, No Stream</li>
        <li><strong>Tuesday:</strong> Day-Off, No Stream</li>
        <li><strong>Wednesday:</strong> Streaming from <strong>4:00 PM EST</strong> for 3 hours</li>
        <li><strong>Thursday:</strong> Streaming from <strong>9:00 AM EST</strong> for 3 hours</li>
        <li><strong>Friday:</strong> Streaming from <strong>9:00 AM EST</strong> for 3 hours</li>
        <li><strong>Saturday:</strong> Variable Stream — starts at either <strong>9:00 AM</strong> or <strong>6:00 PM EST</strong></li>
      </ul>
      <p style={{ marginTop: 15 }}>
        Check for updates at{" "}
        <a href="https://www.x.com/xaelserpent" target="_blank" rel="noreferrer">
          x.com/xaelserpent
        </a>{" "}
        for any incidental changes to the schedule.
      </p>
    </ModalShell>
  );
}

export function HeaderModuleModal({ module, onClose }) {
  if (!module) return null;
  if (module.type === "profile") {
    return <ProfileModuleModal module={module} onClose={onClose} />;
  }
  return (
    <ModalShell id={module.key} title={module.label} onClose={onClose}>
      <div
        className={`xs-header-module-content xs-header-module-${module.type}`}
        dangerouslySetInnerHTML={{ __html: module.content || "" }}
      />
    </ModalShell>
  );
}
